/** Synced lyrics: the panel follows the playhead only when the current line leaves the visible part of the list (never
 * on every tick, never moving the page); any wheel, touch, scroll-key or scrollbar scroll by the user pauses that
 * for 5 s, and a "Follow" chip resumes it at once. Click a line to seek; Shift-click sets In (120 ms before its first
 * word), Alt-click sets Out (its last word + 250 ms hold + 700 ms fade). Raw/masked toggle in the URL; source,
 * confidence and lock state (editing lives on the Song page). */
import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { fmtMs } from "../../lib/frames";
import type { FrameController } from "../player/controller";
import type { InOut } from "../player/Player";
import { usePlayerSelect } from "../player/usePlayer";
import { clickAction, lineAt, lineEdges, lineText, type TextMode } from "../timeline/project";
import type { LyricsOut, Timeline, TimelineLine } from "../types";
import { useLyricsInfo } from "./data";

export const FOLLOW_PAUSE_MS = 5000;
/** Scroll events this soon after our own scrollTo are ours (smooth scrolling fires several). */
const OWN_SCROLL_MS = 800;
const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "]);

export function lyricsMeta(tl: Timeline, info: LyricsOut | undefined): string {
  const words = tl.words;
  const aligned = words.length ? words.filter((w) => w.flag === "aligned").length / words.length : null;
  const confs = (info?.words ?? []).map((w) => w.conf).filter((c): c is number => typeof c === "number");
  const conf = confs.length ? confs.reduce((a, b) => a + b, 0) / confs.length : null;
  const parts: string[] = [];
  const f = info?.final;
  parts.push(f ? `Source: ${f.source}` : "Source: unknown");
  parts.push(conf != null ? `confidence ${(conf * 100).toFixed(0)}%` : "confidence unknown");
  if (aligned != null) parts.push(`${(aligned * 100).toFixed(0)}% of words aligned`);
  parts.push(f ? (f.locked ? `locked${f.locked_by ? ` by ${f.locked_by}` : ""}` : "not locked") : "lock state unknown");
  return parts.join(" · ");
}

/** Top of `el` inside the scroll container `list` (in the list's scroll coordinates). */
export function topInList(list: HTMLElement, el: HTMLElement): number {
  return el.offsetParent === list ? el.offsetTop : el.offsetTop - list.offsetTop;
}

/** The new scrollTop that brings the line into view (a third from the top), or null while it is visible. */
export function followTarget(scrollTop: number, clientHeight: number, top: number, height: number): number | null {
  if (top >= scrollTop && top + height <= scrollTop + clientHeight) return null;
  return Math.max(0, Math.round(top - clientHeight / 3));
}

/** Follow state: paused by the user's own scrolling for FOLLOW_PAUSE_MS, resumed by the timer or `resume()`. */
export function useFollowPause(pauseMs = FOLLOW_PAUSE_MS) {
  const [paused, setPaused] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => {
    if (timer.current != null) clearTimeout(timer.current);
    timer.current = null;
  };
  const pause = useCallback(() => {
    clear();
    setPaused(true);
    timer.current = setTimeout(() => {
      timer.current = null;
      setPaused(false);
    }, pauseMs);
  }, [pauseMs]);
  const resume = useCallback(() => {
    clear();
    setPaused(false);
  }, []);
  useEffect(() => clear, []);
  return { paused, pause, resume };
}

export function LyricsPanel({ tl, ctrl, mode, setMode, agent, inOut, announce }: {
  tl: Timeline; ctrl: FrameController; mode: TextMode; setMode: (m: TextMode) => void; agent: boolean;
  inOut?: InOut; announce?: (msg: string) => void;
}) {
  const info = useLyricsInfo(tl.song_id);
  const cur = usePlayerSelect(ctrl, (s) => lineAt(tl.lines, s.tMs)?.idx ?? -1);
  const listRef = useRef<HTMLOListElement>(null);
  const ownScrollUntil = useRef(0);
  const { paused, pause, resume } = useFollowPause();

  useEffect(() => {
    if (paused) return;
    const list = listRef.current;
    const el = list?.querySelector<HTMLElement>(`[data-line="${cur}"]`);
    if (!list || !el) return;
    const next = followTarget(list.scrollTop, list.clientHeight, topInList(list, el), el.offsetHeight);
    if (next == null || next === list.scrollTop) return;
    ownScrollUntil.current = Date.now() + OWN_SCROLL_MS;
    list.scrollTo({ top: next, behavior: agent ? "auto" : "smooth" });
  }, [cur, agent, paused]);

  const userScroll = () => pause();
  const onScroll = () => {
    if (Date.now() > ownScrollUntil.current) pause();   // the scrollbar, or anything else that was not us
  };
  const onLine = (e: MouseEvent<HTMLButtonElement>, l: TimelineLine) => {
    if (l.t0_mv_ms == null) return;
    const act = inOut ? clickAction(e) : "seek";
    const ed = lineEdges(l.t0_mv_ms, l.t1_mv_ms ?? l.t0_mv_ms);
    if (act === "in" && inOut) {
      inOut.set({ in: ed.inMs });
      announce?.(`In set to ${fmtMs(ed.inMs)} (line ${l.idx + 1})`);
    } else if (act === "out" && inOut) {
      e.preventDefault();
      inOut.set({ out: ed.outMs });
      announce?.(`Out set to ${fmtMs(ed.outMs)} (line ${l.idx + 1})`);
    } else ctrl.seekMs(l.t0_mv_ms);
  };

  let lastSection: string | null | undefined;
  return (
    <section className="ws8-panel" aria-labelledby="ws8-lyrics-h">
      <h2 id="ws8-lyrics-h" className="ws8-h2">Lyrics</h2>
      <div className="ws8-row" role="group" aria-label="Lyrics text">
        <button type="button" data-testid="text-masked" aria-pressed={mode === "masked"} onClick={() => setMode("masked")}>
          Masked
        </button>
        <button type="button" data-testid="text-raw" aria-pressed={mode === "raw"} onClick={() => setMode("raw")}>
          Raw
        </button>
        {paused ? (
          <button type="button" className="ws8-chip" data-testid="lyrics-follow" onClick={resume}
            aria-label="Follow the playhead again (paused because you scrolled)">
            Follow
          </button>
        ) : <span className="ws8-muted" data-testid="lyrics-following">Following the playhead</span>}
      </div>
      <p className="ws8-muted" data-testid="lyrics-meta">
        {lyricsMeta(tl, info.data)}
        {info.isError ? " (lyrics details unavailable)" : ""}.{" "}
        {tl.song_id != null && <Link to={`/songs/${tl.song_id}`} data-testid="song-link">Lock or paste on the Song page</Link>}
      </p>
      {inOut && <p className="ws8-muted">Shift-click a line: In · Alt-click: Out.</p>}
      {tl.lyrics_clock === "song" && tl.lines.some((l) => l.t0_song_ms != null) && (
        <p className="ws8-warn" role="note" data-testid="lyrics-unaligned">
          The MV is not aligned to its song yet: line times are on the song clock and cannot be seeked.
        </p>
      )}
      {tl.lines.length === 0 ? <p data-testid="no-lyrics">No lyrics on this timeline.</p> : (
        <ol ref={listRef} className="ws8-lyrics" data-testid="lyrics-list" onWheel={userScroll}
          onTouchMove={userScroll} onScroll={onScroll}
          onKeyDown={(e) => { if (SCROLL_KEYS.has(e.key)) userScroll(); }}>
          {tl.lines.map((l) => {
            const head = l.section !== lastSection ? l.section : null;
            lastSection = l.section;
            return (
              <li key={l.idx} data-line={l.idx}>
                {head && <span className="ws8-lyrics-section">{head}</span>}
                <button type="button" data-testid={`lyric-${l.idx}`} aria-current={cur === l.idx ? "true" : undefined}
                  disabled={l.t0_mv_ms == null} onClick={(e) => onLine(e, l)}
                  aria-label={`${lineText(l, mode)}${l.t0_mv_ms != null ? ` at ${fmtMs(l.t0_mv_ms)}`
                    : l.t0_song_ms != null ? ` at song ${fmtMs(l.t0_song_ms)} (not aligned to the MV)` : " (not timed)"}`}>
                  <span className="ws8-muted">
                    {l.t0_mv_ms != null ? fmtMs(l.t0_mv_ms) : l.t0_song_ms != null ? `song ${fmtMs(l.t0_song_ms)}` : "–"}
                  </span>{" "}
                  {lineText(l, mode)}
                  {l.performer ? <span className="ws8-muted"> · {l.performer}</span> : null}
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
